﻿import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../app/api";
import type { UiSettings } from "../../app/ui-settings";
import type { AuthUser } from "../../app/auth";

type TopbarProps = {
  settings: UiSettings;
  authUser: AuthUser;
  faciliooSyncBadge?: {
    tone: "ok" | "error";
    label: string;
  } | null;
  demoTrialBadgeLabel?: string | null;
  onLogout: () => void;
  onOpenMenu?: () => void;
};

type SearchArea = "resolutions" | "objects" | "units" | "owners";

type SearchResult = {
  id: string;
  area: SearchArea;
  title: string;
  subtitle: string;
  href: string;
};

const areaLabel = (area: SearchArea) => {
  if (area === "resolutions") return "Beschlüsse";
  if (area === "objects") return "Objekte";
  if (area === "units") return "Einheiten";
  return "Eigentümer";
};

const AreaIcon = ({ area }: { area: SearchArea }) => {
  if (area === "resolutions") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="5" y="4" width="14" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
        <path d="M8 9h8M8 13h8M8 17h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  if (area === "objects") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M3 10 12 4l9 6v10H3z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        <path d="M9 20v-6h6v6" stroke="currentColor" strokeWidth="1.8" />
      </svg>
    );
  }
  if (area === "units") {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M4 4h16v16H4z" stroke="currentColor" strokeWidth="1.8" />
        <path d="M12 4v16M4 12h16" stroke="currentColor" strokeWidth="1.8" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.8" />
      <path d="M5 20a7 7 0 0 1 14 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
};

export const Topbar = ({ settings, authUser, faciliooSyncBadge, demoTrialBadgeLabel, onLogout, onOpenMenu }: TopbarProps) => {
  const navigate = useNavigate();
  const hasLogo = !!settings.logoDataUrl;
  const fallbackName = settings.management.firmenname?.trim();
  const isFrameworkV2 = settings.management.uiFramework === "mockup_v2";
  const [searchText, setSearchText] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searchHasMore, setSearchHasMore] = useState(false);
  const [activeResultIndex, setActiveResultIndex] = useState(0);
  const searchWrapRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const requestSeqRef = useRef(0);

  const roleLabel = authUser.permissionGroupName?.trim()
    ? authUser.permissionGroupName.trim()
    : authUser.role === "superadmin"
      ? "Superadmin"
      : authUser.role === "platform_admin"
        ? "Platform Admin"
        : authUser.role === "platform_superadmin"
          ? "Platform Superadmin"
          : authUser.role === "platform_ops"
            ? "Platform Ops"
            : authUser.role === "sachbearbeitung"
              ? "Sachbearbeitung"
              : "Lesend";

  const canSearchResolutions = authUser.permissions.resolutions !== false;
  const canSearchObjects = authUser.permissions.objects !== false;
  const canSearchUnits = authUser.permissions.units !== false;
  const canSearchOwners = authUser.permissions.owners !== false;

  useEffect(() => {
    if (!isFrameworkV2) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        event.stopPropagation();
        setSearchOpen(true);
        searchInputRef.current?.focus();
        searchInputRef.current?.select();
      }
      if (event.key === "Escape") setSearchOpen(false);
    };
    window.addEventListener("keydown", onKeyDown, { capture: true });
    return () => window.removeEventListener("keydown", onKeyDown, { capture: true });
  }, [isFrameworkV2]);

  useEffect(() => {
    if (!isFrameworkV2) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!searchWrapRef.current) return;
      const target = event.target as Node | null;
      if (!target) return;
      if (!searchWrapRef.current.contains(target)) setSearchOpen(false);
    };
    window.addEventListener("mousedown", onPointerDown);
    return () => window.removeEventListener("mousedown", onPointerDown);
  }, [isFrameworkV2]);

  useEffect(() => {
    if (!isFrameworkV2) return;
    const query = searchText.trim();
    if (query.length < 2) {
      setSearchLoading(false);
      setSearchResults([]);
      setSearchHasMore(false);
      setActiveResultIndex(0);
      return;
    }

    const reqId = ++requestSeqRef.current;
    const timerId = window.setTimeout(async () => {
      setSearchLoading(true);
      const safe = async <T,>(promise: Promise<T>): Promise<T | null> => {
        try {
          return await promise;
        } catch {
          return null;
        }
      };

      const [resolutionsRes, objectsRes, unitsRes, ownersRes] = await Promise.all([
        canSearchResolutions ? safe(api.resolutions({ page: 1, pageSize: 10, search: query })) : Promise.resolve(null),
        canSearchObjects ? safe(api.objects({ page: 1, pageSize: 10, search: query })) : Promise.resolve(null),
        canSearchUnits ? safe(api.units({ page: 1, pageSize: 10, search: query })) : Promise.resolve(null),
        canSearchOwners ? safe(api.owners({ page: 1, pageSize: 10, search: query })) : Promise.resolve(null)
      ]);

      if (requestSeqRef.current !== reqId) return;

      const nextResults: SearchResult[] = [
        ...(resolutionsRes?.data ?? []).map((row) => ({
          id: `resolution-${row.id}`,
          area: "resolutions" as const,
          title: row.titel,
          subtitle: `${row.beschlussnummer} · ${row.objektnummer}`,
          href: `/resolutions/${row.id}`
        })),
        ...(objectsRes?.data ?? []).map((row) => ({
          id: `object-${row.id}`,
          area: "objects" as const,
          title: row.objektname,
          subtitle: `${row.objektnummer} · ${row.strasse} ${row.plz} ${row.ort}`.trim(),
          href: `/objects/${row.id}`
        })),
        ...(unitsRes?.data ?? []).map((row) => ({
          id: `unit-${row.id}`,
          area: "units" as const,
          title: `${row.einheitsnummer} · ${row.bezeichnung}`,
          subtitle: `${row.objektnummer} · ${row.einheitstyp}`,
          href: `/units?search=${encodeURIComponent(row.einheitsnummer || row.bezeichnung)}`
        })),
        ...(ownersRes?.data ?? []).map((row) => ({
          id: `owner-${row.id}`,
          area: "owners" as const,
          title: row.vollname || row.eigentuemer_id,
          subtitle: `${row.eigentuemer_id} · ${row.objektnummer}`,
          href: `/owners?search=${encodeURIComponent(row.eigentuemer_id || row.vollname)}`
        }))
      ];

      const totalAvailable =
        Number(resolutionsRes?.meta?.total ?? 0) +
        Number(objectsRes?.meta?.total ?? 0) +
        Number(unitsRes?.meta?.total ?? 0) +
        Number(ownersRes?.meta?.total ?? 0);
      setSearchResults(nextResults.slice(0, 40));
      setSearchHasMore(totalAvailable > nextResults.length);
      setActiveResultIndex(0);
      setSearchLoading(false);
    }, 180);

    return () => window.clearTimeout(timerId);
  }, [canSearchObjects, canSearchOwners, canSearchResolutions, canSearchUnits, isFrameworkV2, searchText]);

  const activeResult = useMemo(() => {
    if (!searchResults.length) return null;
    const safeIndex = Math.max(0, Math.min(activeResultIndex, searchResults.length - 1));
    return searchResults[safeIndex] ?? null;
  }, [activeResultIndex, searchResults]);

  const demoTrialDaysLeft = useMemo(() => {
    const label = String(demoTrialBadgeLabel ?? "").trim();
    const match = label.match(/(\d+)\s*Tage?/i);
    if (!match) return null;
    const days = Number(match[1]);
    return Number.isFinite(days) ? days : null;
  }, [demoTrialBadgeLabel]);

  const demoTrialToneClass = demoTrialDaysLeft !== null && demoTrialDaysLeft > 10 ? "is-ok" : "is-warn";

  const openSearchResult = (href: string) => {
    navigate(href);
    setSearchOpen(false);
  };

  return (
    <header className={`topbar ${isFrameworkV2 ? "topbar--framework-v2" : ""}`}>
      <div className="topbar-left">
        <button type="button" className="topbar-menu-btn" onClick={onOpenMenu} aria-label="Menü öffnen">
          <span />
          <span />
          <span />
        </button>
        {!isFrameworkV2 && hasLogo ? (
          <img src={settings.logoDataUrl as string} alt="Logo" className="topbar-logo" />
        ) : !isFrameworkV2 && fallbackName ? (
          <strong>{fallbackName}</strong>
        ) : null}
      </div>
      {isFrameworkV2 ? (
        <div className="topbar-search-cluster">
          {demoTrialBadgeLabel ? (
            <span className={`topbar-trial-badge-v2 ${demoTrialToneClass}`}>{demoTrialBadgeLabel}</span>
          ) : null}
          <div className="topbar-search-wrap" aria-label="Suche" ref={searchWrapRef}>
            <span className="topbar-search-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="1.9" />
                <path d="m16 16 4.5 4.5" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
              </svg>
            </span>
            <input
              ref={searchInputRef}
              className="topbar-search-input"
              placeholder="Suche..."
              value={searchText}
              onFocus={() => setSearchOpen(true)}
              onChange={(event) => {
                setSearchText(event.target.value);
                setSearchOpen(true);
              }}
              onKeyDown={(event) => {
                if (!searchOpen) return;
                if (event.key === "ArrowDown") {
                  event.preventDefault();
                  setActiveResultIndex((current) => Math.min(current + 1, Math.max(0, searchResults.length - 1)));
                } else if (event.key === "ArrowUp") {
                  event.preventDefault();
                  setActiveResultIndex((current) => Math.max(current - 1, 0));
                } else if (event.key === "Enter" && activeResult) {
                  event.preventDefault();
                  openSearchResult(activeResult.href);
                } else if (event.key === "Escape") {
                  event.preventDefault();
                  setSearchOpen(false);
                }
              }}
            />
            <span className="topbar-search-shortcut">Ctrl K</span>
            {searchOpen ? (
              <div className="topbar-search-results" role="listbox" aria-label="Suchergebnisse">
                {!searchLoading && searchText.trim().length < 2 ? (
                  <div className="topbar-search-state">Mindestens 2 Zeichen eingeben</div>
                ) : null}
                {!searchLoading && searchText.trim().length >= 2 && searchResults.length === 0 ? (
                  <div className="topbar-search-state">Keine Treffer</div>
                ) : null}
                {!searchLoading && searchResults.length > 0
                  ? searchResults.map((result, index) => (
                      <button
                        key={result.id}
                        type="button"
                        className={`topbar-search-result-item ${index === activeResultIndex ? "active" : ""}`}
                        onMouseEnter={() => setActiveResultIndex(index)}
                        onClick={() => openSearchResult(result.href)}
                      >
                        <span className="topbar-search-result-icon" aria-hidden="true">
                          <AreaIcon area={result.area} />
                        </span>
                        <span className="topbar-search-result-copy">
                          <strong>{result.title}</strong>
                          <span>{areaLabel(result.area)} · {result.subtitle}</span>
                        </span>
                      </button>
                    ))
                  : null}
                {!searchLoading && searchText.trim().length >= 2 ? (
                  <button
                    type="button"
                    className="topbar-search-all-link"
                    onClick={() => openSearchResult(`/search?q=${encodeURIComponent(searchText.trim())}`)}
                  >
                    {searchHasMore ? "Alle Ergebnisse anzeigen" : "Zur Suchseite"}
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
      <div className="topbar-right">
        {!isFrameworkV2 && faciliooSyncBadge ? (
          <span className={`badge topbar-sync-badge ${faciliooSyncBadge.tone === "ok" ? "topbar-sync-badge-ok" : "topbar-sync-badge-error"}`}>
            {faciliooSyncBadge.label}
          </span>
        ) : null}
        {!isFrameworkV2 && demoTrialBadgeLabel ? <span className="badge topbar-trial-badge">{demoTrialBadgeLabel}</span> : null}
        {!isFrameworkV2 ? (
          <>
            <div className="topbar-user">
              <strong className="topbar-user-name">{authUser.displayName}</strong>
              <span className="topbar-user-role">{roleLabel}</span>
            </div>
            <button type="button" className="btn topbar-logout-btn" onClick={onLogout}>
              Abmelden
            </button>
          </>
        ) : null}
      </div>
    </header>
  );
};
